(() => {
  'use strict';
  if (window.rudcaPolishBrowse || !window.fetch || !window.AbortController) return;
  window.rudcaPolishBrowse = true;
  const selector = '.rudca-polish-pilot[data-rudca-compact-browse][data-rudca-polish-theme="188434383074"]';
  const states = new Map();
  const cleanPath = (path) => path.replace(/\/$/, '');
  const escapedType = (value) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  const filterPairs = (pairs, typeValues) => pairs.map(([key, value]) => {
    value = /\.price\.(gte|lte)$/.test(key) ? String(Number(value)) : String(value);
    if (key === 'filter.p.product_type' && typeValues && !typeValues.has(value)) {
      const exact = [...typeValues].find((candidate) => escapedType(candidate) === value);
      if (exact !== undefined) value = exact;
    }
    return [key, value];
  }).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
  const contextKey = (context, typeValues) => JSON.stringify([context.sort, Number(context.pageSize), filterPairs(context.filters, typeValues)]);
  const urlFilters = (url) => [...url.searchParams].filter(([key, value]) => key.startsWith('filter.') && value !== '');
  // Shopify can omit selected types from active_values. Only reconcile a strict
  // known-type subset; every other filter, sorting and page-size constraint stays exact.
  const cappedTypeContext = (rendered, expected, typeValues) => {
    if (!Array.isArray(rendered.filters) || rendered.sort !== expected.sort || Number(rendered.pageSize) !== Number(expected.pageSize)) return false;
    const actual = filterPairs(rendered.filters, typeValues);
    const wanted = filterPairs(expected.filters, typeValues);
    const actualTypes = actual.filter(([key]) => key === 'filter.p.product_type').map(([, value]) => value);
    const wantedTypes = wanted.filter(([key]) => key === 'filter.p.product_type').map(([, value]) => value);
    const selected = new Set(wantedTypes);
    if (!actualTypes.length || actualTypes.length >= wantedTypes.length || selected.size !== wantedTypes.length || new Set(actualTypes).size !== actualTypes.length || !wantedTypes.every((value) => typeValues.has(value)) || !actualTypes.every((value) => selected.has(value))) return false;
    const otherFilters = (pairs) => pairs.filter(([key]) => key !== 'filter.p.product_type');
    return JSON.stringify(otherFilters(actual)) === JSON.stringify(otherFilters(wanted));
  };
  const urlKey = (url) => {
    const params = [...url.searchParams].filter(([key]) => !['page', 'section_id', 'preview_theme_id', '_fd', 'pb'].includes(key)).sort();
    return JSON.stringify([url.origin, cleanPath(url.pathname), params]);
  };
  const cards = (grid) => [...grid.children].filter((node) => node.matches('.product-item[data-rudca-product-id]'));
  const fill = (text, values) => Object.entries(values).reduce((result, [key, value]) => result.split(`[${key}]`).join(String(value)), text);
  const currentContext = (state, url = new URL(location.href)) => {
    if (url.origin !== location.origin || cleanPath(url.pathname) !== cleanPath(state.section.dataset.rudcaPolishUrl)) return false;
    const filters = urlFilters(url);
    return (url.searchParams.get('sort_by') || state.context.defaultSort) === state.context.sort && JSON.stringify(filterPairs(filters)) === JSON.stringify(filterPairs(state.context.filters, state.typeValues));
  };
  const validURL = (state, value, page) => {
    if (!value) return null;
    const url = new URL(value, location.href);
    if (!currentContext(state, url) || Number(url.searchParams.get('page') || 1) !== page || url.searchParams.has('section_id')) return null;
    if (new URL(location.href).searchParams.has('preview_theme_id')) url.searchParams.set('preview_theme_id', new URL(location.href).searchParams.get('preview_theme_id'));
    url.hash = '';
    return url;
  };
  // A native pagination href must independently carry the full bound selection.
  // The final page has no next link, so its server-rendered previous link is the proof.
  const paginationProof = (state, pager, page) => {
    if (pager.dataset.nextUrl) return !!validURL(state, pager.dataset.nextUrl, page + 1);
    const previous = pager.querySelector('[data-rudca-polish-previous][href]');
    return page > 1 && !!validURL(state, previous?.getAttribute('href'), page - 1);
  };
  function setBusy(state, busy) {
    state.busy = busy;
    state.button.setAttribute('aria-disabled', String(busy || state.suspended));
    state.button.textContent = busy ? state.pager.dataset.loading : state.pager.dataset.loadMore;
    state.grid.setAttribute('aria-busy', String(busy));
  }
  function cancel(state, suspend = false) {
    state.version += 1;
    state.controller?.abort();
    state.controller = null;
    state.suspended = suspend;
    setBusy(state, false);
    if (state.status.textContent === state.pager.dataset.loading) {
      state.status.textContent = '';
      state.status.hidden = true;
    }
    if (suspend) { state.button.hidden = true; state.native.hidden = false; }
  }
  function fail(state) {
    state.status.hidden = false;
    state.status.textContent = state.pager.dataset.error;
    state.native.hidden = false;
  }
  const layoutObserver = new MutationObserver((records) => {
    for (const { target: button } of records) {
      if (button.isConnected) button.setAttribute('aria-pressed', String(button.classList.contains('is-selected')));
    }
  });
  function syncLayoutButtons(section) {
    section.querySelectorAll('[data-action="change-layout"]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.classList.contains('is-selected')));
      layoutObserver.observe(button, { attributes: true, attributeFilter: ['class'] });
    });
  }
  function syncChipSelection(section) {
    const selectedTypes = [...new Set(new URL(location.href).searchParams.getAll('filter.p.product_type'))].sort();
    section.querySelectorAll('.rudca-polish-chip[href]').forEach((chip) => {
      const target = new URL(chip.href, location.href);
      const chipTypes = [...new Set(target.searchParams.getAll('filter.p.product_type'))].sort();
      const selected = target.origin === location.origin && cleanPath(target.pathname) === cleanPath(location.pathname) && JSON.stringify(chipTypes) === JSON.stringify(selectedTypes);
      if (selected) chip.setAttribute('aria-current', 'true');
      else chip.removeAttribute('aria-current');
    });
  }
  function initialize(section) {
    syncLayoutButtons(section);
    syncChipSelection(section);
    const grid = section.querySelector('[data-rudca-polish-grid]');
    const pager = section.querySelector('[data-rudca-polish-pagination]');
    const old = states.get(section);
    if (old?.grid === grid && old?.pager === pager) return;
    if (old) cancel(old, true);
    states.delete(section);
    if (!grid || !pager) return;
    let context;
    try { context = JSON.parse(pager.dataset.context); } catch (_) { return; }
    if (!Array.isArray(context.filters) || !Number.isInteger(Number(context.pageSize))) return;
    const button = pager.querySelector('[data-rudca-polish-load-more]');
    const status = pager.querySelector('[data-rudca-polish-status]');
    const native = pager.querySelector('[data-rudca-polish-native]');
    if (!button || !status || !native) return;
    let availableTypes = [];
    try { availableTypes = JSON.parse(section.dataset.rudcaBrowseTypes || '[]'); } catch (_) { return; }
    if (!Array.isArray(availableTypes)) return;
    const typeValues = new Set(availableTypes.filter((value) => typeof value === 'string'));
    const authoritativeTypes = typeValues.size > 0;
    if (!typeValues.size) section.querySelectorAll('.rudca-polish-chip[href]').forEach((chip) => new URL(chip.href, location.href).searchParams.getAll('filter.p.product_type').forEach((value) => typeValues.add(value)));
    const state = { section, grid, pager, context, typeValues, authoritativeTypes, cappedTypes: false, button, status, native, page: Number(pager.dataset.page), offset: Number(pager.dataset.offset), total: Number(pager.dataset.total), version: 0, busy: false, suspended: false, controller: null };
    const currentURL = new URL(location.href);
    if (state.page !== Number(currentURL.searchParams.get('page') || 1)) return;
    if (!currentContext(state)) {
      const requestedContext = { ...context, filters: urlFilters(currentURL) };
      if (!authoritativeTypes || !cappedTypeContext(context, requestedContext, typeValues)) return;
      // Bind once. Later URL changes must still fail the exact currentContext guard.
      state.context = requestedContext;
      state.cappedTypes = true;
      if (!currentContext(state) || !paginationProof(state, pager, state.page)) return;
    }
    state.next = validURL(state, pager.dataset.nextUrl, state.page + 1);
    if (pager.dataset.nextUrl && !state.next) return;
    states.set(section, state);
    native.hidden = true;
    button.hidden = !state.next;
    pager.querySelector('[data-rudca-polish-previous]')?.removeAttribute('hidden');
    setBusy(state, false);
  }
  async function load(state) {
    if (state.busy || state.suspended || !state.next || !currentContext(state)) return;
    const url = state.next;
    const version = ++state.version;
    const startURL = urlKey(new URL(location.href));
    const controller = state.controller = new AbortController();
    const live = () => state.version === version && !state.suspended && !controller.signal.aborted && state.grid.isConnected && states.get(state.section) === state && state.section.querySelector('[data-rudca-polish-grid]') === state.grid && urlKey(new URL(location.href)) === startURL && currentContext(state);
    setBusy(state, true);
    state.status.hidden = false;
    state.status.textContent = state.pager.dataset.loading;
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url.href, { method: 'GET', credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Page request failed');
      if (response.url && !validURL(state, response.url, state.page + 1)) throw new Error('Unexpected page destination');
      const html = await response.text();
      if (!live()) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const incomingSection = [...parsed.querySelectorAll(selector)].find((node) => node.dataset.sectionId === state.section.dataset.sectionId && node.dataset.rudcaPolishCollection === state.section.dataset.rudcaPolishCollection && node.dataset.rudcaPolishUrl === state.section.dataset.rudcaPolishUrl);
      const incomingGrid = incomingSection?.querySelector('[data-rudca-polish-grid]');
      const incomingPager = incomingSection?.querySelector('[data-rudca-polish-pagination]');
      if (!incomingGrid || !incomingPager || Number(incomingPager.dataset.page) !== state.page + 1 || Number(incomingGrid.dataset.rudcaPolishPage) !== state.page + 1) throw new Error('Unexpected page content');
      const incomingContext = JSON.parse(incomingPager.dataset.context);
      const exactContext = contextKey(incomingContext, state.typeValues) === contextKey(state.context, state.typeValues);
      const cappedContext = state.authoritativeTypes && cappedTypeContext(incomingContext, state.context, state.typeValues);
      if ((!exactContext && !cappedContext) || ((state.cappedTypes || cappedContext) && !paginationProof(state, incomingPager, state.page + 1)) || Number(incomingPager.dataset.total) !== state.total) throw new Error('Selection changed');
      const next = validURL(state, incomingPager.dataset.nextUrl, state.page + 2);
      if (incomingPager.dataset.nextUrl && !next) throw new Error('Unexpected next page');
      const seen = new Set(cards(state.grid).map((card) => card.dataset.rudcaProductId));
      const additions = cards(incomingGrid).filter((card) => {
        const id = card.dataset.rudcaProductId;
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      if (!additions.length) throw new Error('No new products');
      const fragment = document.createDocumentFragment();
      const activeLayout = state.section.querySelector('[data-action="change-layout"].is-selected')?.dataset.layoutMode;
      for (const card of additions) {
        if (activeLayout) {
          card.classList.toggle('product-item--list', activeLayout === 'list');
          card.classList.toggle('product-item--vertical', activeLayout !== 'list');
        }
        fragment.appendChild(document.importNode(card, true));
      }
      if (!live()) return;
      const firstAdded = fragment.firstElementChild;
      const keepFocus = document.activeElement === state.button;
      const x = window.scrollX;
      const y = window.scrollY;
      state.grid.appendChild(fragment);
      state.page += 1;
      state.grid.dataset.rudcaPolishPage = String(state.page);
      state.next = next;
      state.native.innerHTML = incomingPager.querySelector('[data-rudca-polish-native]')?.innerHTML || '';
      state.native.hidden = true;
      state.button.hidden = !next;
      const shown = cards(state.grid).length;
      state.status.textContent = fill(state.pager.dataset.shown, { shown, total: state.total }) + (next ? '' : ` ${state.pager.dataset.complete}`);
      const range = state.section.querySelector('[data-rudca-polish-showing]');
      if (range) range.textContent = fill(state.pager.dataset.range, { first: state.offset + 1, last: state.offset + shown, total: state.total });
      if (keepFocus) (firstAdded.querySelector('.product-item__title[href], a[href]') || state.status).focus({ preventScroll: true });
      if (window.scrollX !== x || window.scrollY !== y) window.scrollTo(x, y);
      state.grid.dispatchEvent(new CustomEvent('rudca:products-appended', { bubbles: true, detail: { count: additions.length, page: state.page } }));
      window.dispatchEvent(new Event('resize'));
    } catch (error) {
      if (state.version === version && !state.suspended && state.grid.isConnected && states.get(state.section) === state && urlKey(new URL(location.href)) === startURL) fail(state);
    } finally {
      clearTimeout(timeout);
      if (state.version === version) { state.controller = null; setBusy(state, false); }
    }
  }
  function refresh() {
    layoutObserver.disconnect();
    for (const [section, state] of states) if (!section.isConnected) { cancel(state, true); states.delete(section); }
    document.querySelectorAll(selector).forEach(initialize);
  }
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    const section = target?.closest(selector);
    const state = section && states.get(section);
    if (!state) return;
    if (target.closest('[data-rudca-polish-load-more]')) {
      event.preventDefault();
      event.stopPropagation();
      load(state);
      return;
    }
    const modifiedClick = event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
    if (target.closest('[data-rudca-polish-native] a[href]')) {
      if (!modifiedClick) cancel(state, true);
      event.stopPropagation();
      return;
    }
    if (target.closest('a[href]') && modifiedClick) return;
    if (target.closest('.rudca-polish-chip, [data-action="clear-filters"], #sort-by-selector [data-action="select-value"], #display-by-selector [data-action="select-value"], .pagination [data-page]')) cancel(state, true);
  }, true);
  document.addEventListener('change', (event) => {
    const target = event.target;
    const section = target.closest?.(selector);
    const state = section && states.get(section);
    if (state && target.matches('[name^="filter."], #mobile-sort-by, #desktop-sort-by, #showing-count')) cancel(state, true);
  }, true);
  window.addEventListener('pagehide', () => states.forEach((state) => cancel(state)));
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    states.forEach((state) => {
      if (!currentContext(state)) return;
      state.suspended = false;
      state.button.hidden = !state.next;
      setBusy(state, false);
      if (state.status.textContent === state.pager.dataset.loading) state.status.textContent = '';
    });
    refresh();
  });
  window.addEventListener('popstate', () => {
    if (!document.querySelector(selector)) return;
    states.forEach((state) => cancel(state, true));
    window.location.reload();
  });
  function start() {
    refresh();
    new MutationObserver(refresh).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
